/**
 * WorkspaceRepairBanner — owner-facing detection and one-click repair for the
 * split-workspace defect (INCIDENT-20260804).
 *
 * A firm can end up with its team records in one account document and its actual
 * workspace — threads, history, memory, VM routing — in another. Team & Roles
 * then shows a team that thread membership cannot see, and adding a colleague to
 * a thread fails with a refusal the admin has no way to act on. This banner is
 * the missing feedback: it names the condition, and repairs it without anyone
 * needing a terminal or a service-account key.
 *
 * Renders NOTHING unless the server reports an actionable split, so it is invisible
 * on every healthy workspace. The repair is preview-then-confirm: the dry run
 * lists exactly what will be copied before anything is written.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  consolidateWorkspace,
  diagnoseWorkspace,
  type WorkspaceConsolidationPlan,
} from '../../services/rbacService';
import ConfirmDialog from './ConfirmDialog';

const Banner = styled.div<{ $tone: 'warn' | 'ok' }>`
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  border-radius: 10px;
  font-size: 0.875rem;
  line-height: 1.5;
  background: ${(p) => (p.$tone === 'ok' ? 'rgba(40, 167, 69, 0.07)' : 'rgba(220, 53, 69, 0.06)')};
  border: 1px solid
    ${(p) => (p.$tone === 'ok' ? 'rgba(40, 167, 69, 0.35)' : 'rgba(220, 53, 69, 0.28)')};
  color: ${(p) => (p.$tone === 'ok' ? '#1e7e34' : '#8a2b34')};
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 0.375rem;
`;

const Detail = styled.div`
  color: #6b5560;
  font-size: 0.8125rem;
  margin-top: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.875rem;
`;

const RepairBtn = styled.button`
  background: var(--brand-gold);
  color: var(--brand-black);
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.125rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;

  &:hover:not(:disabled) {
    background: #b8973f;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: inherit;
  text-decoration: underline;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0;
`;

const Ledger = styled.pre`
  margin: 0.5rem 0 0;
  padding: 0.625rem 0.75rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.5;
  max-height: 190px;
  overflow: auto;
  white-space: pre-wrap;
`;

interface Props {
  /** Called after a successful repair so the members list re-reads. */
  onRepaired?: () => void;
}

const WorkspaceRepairBanner: React.FC<Props> = ({ onRepaired }) => {
  const [plan, setPlan] = useState<WorkspaceConsolidationPlan | null>(null);
  const [preview, setPreview] = useState<string[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Silent on failure by design: a diagnosis that cannot run must never block or
  // alarm a healthy team page.
  useEffect(() => {
    let cancelled = false;
    diagnoseWorkspace()
      .then((res) => {
        if (!cancelled && res?.split) setPlan(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const runPreview = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await consolidateWorkspace({ apply: false });
      setPreview(
        res.ledger
          .filter((l) => l.action === 'copy')
          .map((l) => l.path)
          .slice(0, 60),
      );
      setConfirmOpen(true);
    } catch (err: any) {
      setError(err?.message ?? 'could not preview the repair');
    } finally {
      setBusy(false);
    }
  }, []);

  const runRepair = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await consolidateWorkspace({ apply: true });
      if (res.status === 'applied') {
        const moved = res.plan.memberIdsToCopy.length;
        setDone(
          moved > 0
            ? `repaired — ${moved} team record${moved === 1 ? '' : 's'} moved into this workspace. ` +
              'members can now be added to threads.'
            : 'repaired — the team page now reads from the workspace that owns your threads. ' +
              'reload to see it.',
        );
        setPlan(null);
        setConfirmOpen(false);
        onRepaired?.();
      } else {
        setError(
          res.plan?.blockers?.[0] ?? 'the repair did not run — nothing was changed.',
        );
        setConfirmOpen(false);
      }
    } catch (err: any) {
      setError(err?.message ?? 'the repair failed — nothing was changed.');
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }, [onRepaired]);

  if (done) {
    return (
      <Banner $tone="ok" data-workspace-repair="done">
        <Title>workspace repaired</Title>
        {done}
      </Banner>
    );
  }

  if (!plan?.split) return null;

  const blocked = (plan.blockers?.length ?? 0) > 0;
  const movable = plan.memberIdsToCopy?.length ?? 0;

  return (
    <>
      <Banner $tone="warn" data-workspace-repair="split">
        <Title>this workspace’s team records are in the wrong place</Title>
        {plan.summary}
        <Detail>
          threads, history and memory live in <strong>{plan.targetAccountId}</strong>, but the team
          is stored in <strong>{plan.sourceAccountId}</strong>. that is why someone can appear on
          this page and still be refused when you add them to a thread.
        </Detail>

        {blocked ? (
          <Detail data-workspace-repair-blocked="">
            this needs an administrator: {plan.blockers[0]}
          </Detail>
        ) : (
          <Row>
            <RepairBtn onClick={runPreview} disabled={busy}>
              {busy ? 'checking…' : 'repair workspace'}
            </RepairBtn>
            <LinkBtn onClick={runPreview} disabled={busy}>
              see what will change
            </LinkBtn>
          </Row>
        )}

        {error && <Detail style={{ color: '#c53030' }}>{error}</Detail>}
      </Banner>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="repair workspace"
        confirmLabel={busy ? 'repairing…' : 'repair workspace'}
        cancelLabel="not now"
        busy={busy}
        error={error}
        message={
          <>
            <p>
              {movable > 0 ? (
                <>
                  Move {movable} team record{movable === 1 ? '' : 's'} into{' '}
                  <strong>{plan.targetAccountId}</strong>?
                </>
              ) : (
                <>
                  Re-align this workspace&rsquo;s team reading to{' '}
                  <strong>{plan.targetAccountId}</strong>? Every team record already lives
                  there — this re-points the team page (and any stale member pointers) at the
                  workspace that owns your threads.
                </>
              )}
            </p>
            {preview && preview.length > 0 && (
              <Ledger data-workspace-repair-preview="">{preview.join('\n')}</Ledger>
            )}
          </>
        }
        consequence={
          movable > 0
            ? 'Nothing is deleted and nothing existing is overwritten — records are copied into the ' +
              'workspace that owns your threads, and the old account is left exactly as it is. ' +
              'Members become addable to threads immediately afterwards.'
            : 'Nothing is copied, deleted, or overwritten — only the pointers that decide which ' +
              'account this page reads from are updated. The old account is left exactly as it is.'
        }
        onConfirm={runRepair}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default WorkspaceRepairBanner;
